import React, { useState } from 'react';

import {
  ActionIcon,
  Alert,
  Button,
  Checkbox,
  Flex,
  Modal,
  Paper,
  PaperProps,
  Skeleton,
  Space,
  Text,
  Tooltip
} from '@mantine/core';
import { IconAlertCircle, IconAlertTriangle, IconExternalLink, IconTrash } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { ShopifyIcon } from 'icons/ShopifyIcon';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ResourceService } from 'services/resource-service';
import { RESOURCE_SHOPIFY_STORE_QUERY_KEY } from 'shared/constants/query-keys';
import { ROUTE_LINKS } from 'shared/constants/route-links';
import { ShopifyStore } from 'shared/interfaces/ShopifyStore';

interface ShopifyStoreTileProps extends PaperProps {
  shopifyStore: ShopifyStore;
  selectedResourceStoreId?: string;
  onSelect?: (resourceStoreId: string) => void;
}

export function ShopifyStoreTile({
  shopifyStore,
  selectedResourceStoreId,
  onSelect,
  ...paperProps
}: ShopifyStoreTileProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [isConfigurationNeeded, setIsConfigurationNeeded] = useState<boolean>();

  const resourceShopifyStoreQuery = useQuery({
    queryFn: () => ResourceService.getResourceByShopifyStoreId(shopifyStore._id),
    queryKey: [RESOURCE_SHOPIFY_STORE_QUERY_KEY, { shopifyStoreId: shopifyStore._id }],
    onSuccess: (maybeResource) => {
      const foundResource = !!maybeResource;
      setIsConfigurationNeeded(!foundResource);
    }
  });

  const navigateToStoreDetails = () => navigate(`${ROUTE_LINKS.stores}?store=${shopifyStore._id}`);

  const onTileClick = () => {
    if (onSelect && isConfigurationNeeded === false && resourceShopifyStoreQuery.data?._id) {
      onSelect(resourceShopifyStoreQuery.data._id);
    } else if (!onSelect) {
      navigateToStoreDetails();
    }
  };

  if (resourceShopifyStoreQuery.isLoading) return <Skeleton height={40} w="100%" style={{ borderRadius: '5px' }} />;
  if (resourceShopifyStoreQuery.isError && isConfigurationNeeded === undefined) return null;

  return (
    <>
      <Paper className="g-hover-style" p={5} px={10} withBorder onClick={onTileClick} {...paperProps}>
        <Flex direction="row" justify="space-between" align="center" w="100%" h="100%">
          <Flex direction="row" align="center">
            {selectedResourceStoreId !== undefined && (
              <>
                <Checkbox
                  checked={selectedResourceStoreId === resourceShopifyStoreQuery.data?._id}
                  onChange={onTileClick}
                  size="xs"
                />
                <Space w={10} />
              </>
            )}
            <ShopifyIcon />
            <Space w={10} />
            <Text>{shopifyStore.shop}</Text>
          </Flex>

          <Flex direction="row" align="center">
            {isConfigurationNeeded && (
              <Tooltip color="dark" label={t('STORES.CONFIGURE_TOOLTIP')} position="bottom" withArrow>
                <Button
                  variant="outline"
                  size="xs"
                  color="orange"
                  leftIcon={<IconAlertTriangle />}
                  pr={10}
                  mr="xs"
                  onClick={navigateToStoreDetails}>
                  {t('APP.ACTIONS.CONFIGURE')}
                </Button>
              </Tooltip>
            )}

            <ActionIcon
              color="red"
              onClick={(event) => {
                event.stopPropagation();
                setDeleteModalOpened(true);
              }}>
              <IconTrash size={22} />
            </ActionIcon>
          </Flex>
        </Flex>
      </Paper>
      <Modal
        opened={deleteModalOpened}
        onClose={() => setDeleteModalOpened(false)}
        title={t('STORES.DELETE_STORE.TITLE')}>
        <Alert icon={<IconAlertCircle />} title={t('STORES.DELETE_STORE.IMPORTANT')} color="red">
          <Text>{t('STORES.DELETE_STORE.NOTICE')}</Text>
        </Alert>
        <Button
          my="sm"
          style={{ float: 'right' }}
          rightIcon={<IconExternalLink />}
          onClick={() => window.open('https://admin.shopify.com/', '_blank')}>
          {t('STORES.DELETE_STORE.GO_TO_STORE')}
        </Button>
      </Modal>
    </>
  );
}
